import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ExternalLink, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, fixEncoding, SEO_CONFIG } from "@/utils/helpers";
import { 
  generateComparisonSchema, 
  generateBreadcrumbSchema,
  combineSchemas,
  useStructuredData
} from "@/utils/structuredData";

const ComparisonPage = () => {
  const { ean } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [affilizzOffers, setAfffilizzOffers] = useState([]);
  const [allOffers, setAllOffers] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [productName, setProductName] = useState('');
  const [error, setError] = useState('');
  const [detectedBrand, setDetectedBrand] = useState(null);
  
  const originalQuery = searchParams.get('query') || ean;

  // Fetch brand from spreadsheet database based on query
  useEffect(() => {
    const fetchBrandForQuery = async () => {
      if (!originalQuery) return;
      
      try {
        const response = await axios.get(`${API}/query/brand?q=${encodeURIComponent(originalQuery)}`);
        if (response.data.brand) {
          setDetectedBrand(response.data.brand);
        }
      } catch (err) {
        console.error('Error fetching brand for query:', err);
      }
    };
    
    fetchBrandForQuery();
  }, [originalQuery]);

  useEffect(() => {
    const fetchComparisonData = async () => {
      if (!ean) return;
      
      setLoading(true);
      setError('');
      
      try {
        const kelkooResponse = await axios.get(`${API}/search`, {
          params: {
            query: originalQuery,
            pageSize: 100,
            page: 1,
            filterBy: [`codeEan:${ean}`]
          }
        });
        
        const offers = kelkooResponse.data.offers || [];
        setProducts(offers);
        
        if (offers.length > 0 && offers[0].title) {
          setProductName(fixEncoding(offers[0].title));
        }
        
        const images = offers
          .filter(offer => offer.images && offer.images.length > 0)
          .map(offer => offer.images[0].url)
          .slice(0, 5);
        setProductImages([...new Set(images)]);
        
        const kelkooOffersFormatted = offers.map(offer => ({
          offerId: offer.offerId,
          shopName: fixEncoding(offer.merchant?.name) || 'Marchand',
          shopIcon: offer.merchant?.logoUrl || offer.merchant?.logo || null,
          price: offer.price || 0,
          currency: 'EUR',
          url: offer.goUrl,
          stock: true,
          marketplace: false,
          condition: 'NEW',
          source: 'Kelkoo',
          deliveryCost: offer.deliveryCost
        }));
        
        const eans = [...new Set(offers.map(offer => offer.code?.ean).filter(Boolean))];
        
        let affilizzOffersFormatted = [];
        
        if (eans.length > 0) {
          const affilizzResponse = await axios.post(`${API}/affilizz/compare`, { eans });
          
          if (affilizzResponse.data && affilizzResponse.data.tabs) {
            const affilizzOffers = affilizzResponse.data.tabs.flatMap(tab => 
              (tab.offers || []).map(offer => ({ ...offer, source: 'Affilizz' }))
            );
            setAfffilizzOffers(affilizzOffers);
            affilizzOffersFormatted = affilizzOffers;
            
            if (!productName && affilizzResponse.data.tabs[0]?.productName) {
              setProductName(affilizzResponse.data.tabs[0].productName);
            }
          }
        }
        
        const mergedOffers = [...kelkooOffersFormatted, ...affilizzOffersFormatted]
          .sort((a, b) => a.price - b.price);
        
        setAllOffers(mergedOffers);
      } catch (err) {
        console.error('Error fetching comparison data:', err);
        setError('Erreur lors du chargement des données de comparaison');
      } finally {
        setLoading(false);
      }
    };
    
    fetchComparisonData();
  }, [ean, originalQuery]);

  // SEO structured data for comparison page - comprehensive schemas
  const breadcrumbSchema = productName ? generateBreadcrumbSchema([
    { name: 'Accueil', url: SEO_CONFIG.siteUrl },
    { name: 'Comparateur', url: `${SEO_CONFIG.siteUrl}/compare` },
    { name: productName, url: `${SEO_CONFIG.siteUrl}/compare/${ean}` }
  ]) : null;

  const comparisonSchema = productName && allOffers.length > 0 
    ? generateComparisonSchema(productName, ean, allOffers, productImages)
    : null;
  
  const structuredData = combineSchemas(breadcrumbSchema, comparisonSchema);
  
  // Inject structured data into document head
  useStructuredData(structuredData, 'comparison-structured-data');

  return (
    <div className="comparison-page">
      <Helmet>
        <title>{productName ? `${productName} - Comparateur de prix | ${SEO_CONFIG.siteName}` : `Comparateur de prix | ${SEO_CONFIG.siteName}`}</title>
        <meta name="description" content={`Comparez les prix de ${productName || 'ce produit'} chez ${allOffers.length} marchands. Trouvez le meilleur prix.`} />
        <meta property="og:title" content={`${productName || 'Comparateur de prix'} | ${SEO_CONFIG.siteName}`} />
        <meta property="og:type" content="product" />
      </Helmet>

      <header className="comparison-header">
        <div className="comparison-header-content">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_shop-navigator-13/artifacts/hmk599ai_marques-and-co_logo.jpg" 
              alt="Marques & Co" 
              className="comparison-logo"
            />
          </Link>
        </div>
        
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb-nav" data-testid="breadcrumb">
          <Link to="/" className="breadcrumb-item">
            <HomeIcon className="breadcrumb-icon" />
            <span>Accueil</span>
          </Link>
          <span className="breadcrumb-separator">/</span>
          {detectedBrand && (
            <>
              <Link to={`/brand/${encodeURIComponent(detectedBrand)}`} className="breadcrumb-item">
                {detectedBrand}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          {/* Only show query if it's different from the brand (avoid duplication when coming from brand page) */}
          {originalQuery && originalQuery !== ean && 
           (!detectedBrand || originalQuery.toLowerCase() !== detectedBrand.toLowerCase()) && (
            <>
              <Link to={`/?query=${encodeURIComponent(originalQuery)}&page=1`} className="breadcrumb-item">
                {originalQuery}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current" title={productName || `EAN: ${ean}`}>
            {productName ? (productName.length > 40 ? productName.substring(0, 40) + '...' : productName) : `EAN: ${ean}`}
          </span>
        </nav>
      </header>

      <div className="comparison-container">
        <h1 className="comparison-title">
          {productName || `Comparaison de prix - EAN: ${ean}`}
        </h1>
        
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Chargement de la comparaison...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {productImages.length > 0 && (
              <div className="comparison-images">
                {productImages.map((imageUrl, index) => (
                  <div key={index} className="comparison-image-wrapper">
                    <img src={imageUrl} alt={`Product ${index + 1}`} className="comparison-image" loading="lazy" />
                  </div>
                ))}
              </div>
            )}
          
            {allOffers.length > 0 && (
              <div className="affilizz-widget-container">
                <h2 className="section-title">
                  Comparaison de prix - {allOffers.length} offres trouvées
                </h2>
                <div className="affilizz-price-table">
                  {allOffers.map((offer, index) => (
                    <a
                      key={offer.offerId || index}
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      className={`affilizz-offer-row ${index === 0 ? 'best-price' : ''}`}
                    >
                      <div className="affilizz-merchant">
                        {offer.shopIcon && (
                          <img src={offer.shopIcon} alt={offer.shopName} className="affilizz-merchant-icon" />
                        )}
                        <div className="affilizz-merchant-info">
                          <span className="affilizz-merchant-name">{offer.shopName}</span>
                          <div className="affilizz-badges">
                            {offer.marketplace && (
                              <span className="affilizz-marketplace-badge">Marketplace</span>
                            )}
                            {offer.deliveryCost === 0 && offer.source === 'Kelkoo' && (
                              <span className="affilizz-delivery-badge">Livraison gratuite</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="affilizz-price-info">
                        <span className="affilizz-price">
                          {offer.price.toFixed(2)} €
                          {index === 0 && <span className="best-price-badge">Meilleur prix</span>}
                        </span>
                        {offer.condition && offer.condition !== 'NEW' && (
                          <span className="affilizz-condition">
                            {offer.condition === 'USED' ? 'Occasion' : offer.condition}
                          </span>
                        )}
                      </div>
                      
                      <Button className="affilizz-offer-button">
                        Voir l'offre
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {allOffers.length === 0 && !loading && (
              <div className="info-message">
                <p>ℹ️ Aucune offre trouvée pour ce produit</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ComparisonPage;
